/* eslint-disable no-use-before-define */
import {getProfilePurposes, getPreFilledPurposesFromCookie} from '../purposes';
import {logger} from './logger';
import {getCookie, setCookie, deleteCookie} from './cookie';
import {setConsentCookie} from './consent';
import {setCookieToGlobalStorage} from '../services/globalCookie';
import {ConsentString} from 'consent-string';
import {inProfile} from '../utils/scriptParams';
import {end, profileError, profileSaved} from '../services/measurement';
import {PROFILERROR_COOKIE_NAME} from '../constants';

let currentRusId = null;

/**
 * Sets current rusId from profile data
 *
 * @param {number} id
 */
export const setRusId = id => {
  currentRusId = id;
};

/**
 * Gets current rusId
 */
const getRusId = () => currentRusId;

/**
 * Gets data from Profil.
 * @return {Promise}
 */
export const getProfileData = () =>
  new Promise((resolve, reject) => {
    try {
      const onConsentEvent = e => {
        if (e.detail) {
          logger(1500, 'Got Profile consent data');
          window.removeEventListener('consent', onConsentEvent);
          resolve(e.detail);
        }
      };

      window.addEventListener('consent', onConsentEvent);
      logger(1501, 'Waiting for Profile consent data');
      window.login.consent.get();
    } catch (error) {
      reject(error);
    }
  });

/**
 * We want to save into Profile only public props from ConsentString object.
 * @param {string} encodedCookie
 * @param {ConsentString} data
 */
export const getEuconsentData = (encodedCookie, euconsentData) => ({
  encodedCookie,
  allowedPurposeIds: euconsentData.getPurposesAllowed(),
  allowedVendorIds: euconsentData.getVendorsAllowed(),
  cmpId: euconsentData.getCmpId(),
  cmpVersion: euconsentData.getCmpVersion(),
  consentLanguage: euconsentData.getConsentLanguage(),
  consentScreen: euconsentData.getConsentScreen(),
  created: euconsentData.getCreated().toISOString(),
  lastUpdated: euconsentData.getLastUpdated().toISOString(),
  vendorListVersion: euconsentData.getVendorListVersion(),
  version: euconsentData.getVersion()
});

/**
 * Saves settings into Profil.
 * @param {string} encodedCookie
 * @param {ConsentString} euconsentData
 * @param {Object} purposes
 * @return {Promise}
 */
export const setProfileData = (encodedCookie, euconsentData, purposes) =>
  new Promise((resolve, reject) => {
    const consent = getProfilePurposes(purposes);
    const euconsent = getEuconsentData(encodedCookie, euconsentData);
    let timer = null;

    logger(603, 'Saving data into profile:', {
      consent,
      euconsent
    });

    const onConsentChange = () => {
      removeConsentListeners();
      logger(609, 'Consent successfully changed');
      resolve();
    };

    const onConsentError = () => {
      removeConsentListeners();
      logger(610, 'Consent error occured');
      reject();
    };

    const removeConsentListeners = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      window.removeEventListener('consent-change', onConsentChange);
      window.removeEventListener('consent-error', onConsentError);
    };

    window.addEventListener('consent-change', onConsentChange);
    window.addEventListener('consent-error', onConsentError);

    // if saving fails
    timer = setTimeout(() => {
      removeConsentListeners();
      logger(611, 'Consent save timeout');
      reject();
    }, 5000);

    window.login.consent.set(consent, euconsent);
  });

/**
 * Handles case when data cannot be saved into Profil.
 *
 * @param {string} encodedCookie cookie string
 */
export const saveProfileError = encodedCookie => {
  logger(1502, 'ProfileError: Data could not be saved into Profile');

  const rusId = getRusId();

  if (encodedCookie && typeof rusId === 'number') {
    setCookie(rusId + '|' + encodedCookie, PROFILERROR_COOKIE_NAME);
    logger(1503, 'ProfileError: data saved for later try.');
  }
};

/**
 * Gets ProfileError data
 *
 * @returns {Object} error data
 */
export const getProfileError = () => {
  let data = getCookie(PROFILERROR_COOKIE_NAME);

  if (data) {
    data = data.split('|');
    const rusId = parseInt(data[0], 10);
    const euconsent = data[1];

    data = {
      rusId,
      euconsent
    };

    logger(1504, 'ProfileError: got data', data);
    return data;
  }

  logger(1505, 'ProfileError: no data found');
  return null;
};

/**
 * Deletes ProfileError from local storage
 */
export const clearProfileError = () => deleteCookie(PROFILERROR_COOKIE_NAME);

/**
 * Checks if any ProfileError log exists.
 *
 * @returns {boolean} true if exists, otherwise false
 */
export const existsProfileError = () => getCookie(PROFILERROR_COOKIE_NAME) !== '';

/**
 * Tries to synchronize data from latest ProfileError
 * @param {string} encodedCookie cookie string
 */
export const handleProfileError = async encodedCookie => {
  if (!inProfile()) {
    logger(1506, 'Saving euconsent cookie into browser:', encodedCookie);
    setConsentCookie(encodedCookie);
  }

  const consentData = new ConsentString(encodedCookie);
  const purposes = getPreFilledPurposesFromCookie(encodedCookie);

  setCookieToGlobalStorage(encodedCookie);

  try {
    await setProfileData(encodedCookie, consentData, purposes);
    clearProfileError();
    profileSaved();
    end();
  } catch (error) {
    profileError();
    saveProfileError(encodedCookie);
    end();
  }
};

/**
 * Returns true if cookie create date is outdated.
 * @param {string} date in 2019-07-03T00:00:00 format
 * @param {string} cmpData in 2019-07-03T00:00:00 format
 * @return {boolean}
 */
export const isProfileCookieOutdated = (date, cmpData) => {
  const cookieTimestamp = new Date(date).getTime();
  const cmpTimestamp = new Date(cmpData).getTime();

  return cmpTimestamp > cookieTimestamp;
};
